import { isRoundTrip } from '../travels';
import { ShuttleDetails } from './shuttle-details';
import { ShuttleFilters } from './shuttle-filters';
import { ShuttlePassenger } from './shuttle-passenger';
import { ShuttleRideId, ShuttleRoute } from './shuttle-route';
import { ShuttleSelectedSeat } from './shuttle-seat';

export interface Shuttle {
  arrivalDateTime: string;
  departureDate: string;
  departureDateTime: string;
  destinationId: string;
  destinationText: string;
  groupId: string /** It must be a unique ID */;
  hasBaggage: boolean;
  hasSeats: boolean;
  item_id: string;
  item_name: string;
  originId: string;
  originText: string;
  passengersInfo: ShuttlePassenger[];
  passengersQ: number;
  passengersS: ShuttleSelectedSeat[][];
  price: number;
  provider: string;
  quantity: number /** Prop value must be equal to passengersQ */;
  rideId: ShuttleRideId;
  /**
   *  It's used for shopping cart.
   *  - total = passengersQ * price
   */
  total: number;
  totalExtraBaggages: null;
  totalExtraBaggagesCharges: null;
  type: 'shuttle';
  /**
   *  - Shuttle - One way trip = 1
   *  - Shuttle - Round trip = 2
   */
  typeId: 1 | 2;
}

export interface ShuttleWithoutPassengersInfo extends Omit<Shuttle, 'passengersInfo'> { }

interface BuildShuttleWithoutPassengersInfo extends Pick<Shuttle, 'groupId' | 'passengersS'> {
  shuttleDetails: ShuttleDetails;
  shuttleFilters: ShuttleFilters;
  shuttleRoute: ShuttleRoute;
}

export const buildShuttleWithoutPassengersInfo = ({
  groupId,
  shuttleDetails,
  shuttleFilters,
  shuttleRoute,
  passengersS,
}: BuildShuttleWithoutPassengersInfo): ShuttleWithoutPassengersInfo => {
  const scales = shuttleRoute.scales.length;
  const originText = shuttleRoute.originText ?? shuttleRoute.scales[0].from;
  const destinationText = shuttleRoute.destinationText ?? shuttleRoute.scales[scales - 1].to;
  return {
    arrivalDateTime: shuttleRoute.arrivalDateTime,
    departureDate: shuttleRoute.departureDate,
    departureDateTime: shuttleRoute.departureDateTime,
    destinationId: shuttleRoute.destinationId,
    destinationText: destinationText,
    groupId,
    hasBaggage: shuttleDetails.baggageMapEnabled,
    hasSeats: shuttleDetails.seatsMapEnabled,
    item_id: `Shuttle ${shuttleRoute.provider}`,
    item_name: `Shuttle $${originText} to ${destinationText}`,
    originId: shuttleRoute.originId,
    originText: originText,
    passengersQ: shuttleFilters.passengers,
    passengersS,
    price: shuttleRoute.price,
    provider: shuttleRoute.provider,
    quantity: shuttleFilters.passengers,
    rideId: shuttleRoute.rideId,
    total: shuttleRoute.price * shuttleFilters.passengers,
    totalExtraBaggages: null,
    totalExtraBaggagesCharges: null,
    type: 'shuttle',
    typeId: isRoundTrip(shuttleFilters.travelType) ? 2 /** Round trip */ : 1 /** One way trip */,
  };
};

export const addPassengersInfoToShuttle = (
  shuttleWithoutPassengersInfo: ShuttleWithoutPassengersInfo,
  passengers: ShuttlePassenger[],
): Shuttle => {
  return { ...shuttleWithoutPassengersInfo, passengersInfo: passengers };
};
